/**
 * Engine-internal types: moves, errors, and the private/public state contract.
 */

import type { Transport } from '../types'

/** Transport types a detective can use (no ferry, no black). */
export type DetectiveTransport = 'taxi' | 'bus' | 'underground'

/** A single Mr. X sub-move (atom; not a complete turn — see `Move`). */
export interface MrxSubMove {
  /** Destination station id. */
  toStation: number
  /**
   * The transport edge used. Ferry is allowed only when `useBlackTicket`
   * is true.
   */
  transport: Transport
  /** If true, log this move as `'black'` and consume one black ticket. */
  useBlackTicket: boolean
}

/**
 * A move applied to the engine. Mr. X's double-move is a single atomic move
 * so that round/reveal accounting is correct.
 */
export type Move =
  | {
      kind: 'detective'
      uid: string
      toStation: number
      transport: DetectiveTransport
    }
  | ({
      kind: 'mrx'
    } & MrxSubMove)
  | {
      kind: 'mrx-double'
      first: MrxSubMove
      second: MrxSubMove
    }
  | {
      kind: 'detective-stranded'
      /**
       * Marks a detective as stranded for the rest of the game. Caller may
       * dispatch this when `legalMoves(state, uid)` returns `[]` for a
       * detective whose turn is up. The engine also auto-strands detectives
       * proactively in some flows; callers can rely on either.
       */
      uid: string
    }

/** Error thrown by `applyMove` when validation fails. */
export class EngineError extends Error {
  constructor(
    public readonly code: EngineErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'EngineError'
  }
}

export type EngineErrorCode =
  | 'WRONG_PHASE'
  | 'NOT_AWAITING'
  | 'UNKNOWN_PLAYER'
  | 'GAME_OVER'
  | 'NO_TICKET'
  | 'NO_BLACK_TICKET'
  | 'NO_DOUBLE_MOVE'
  | 'NO_EDGE'
  | 'OCCUPIED_BY_DETECTIVE'
  | 'FERRY_REQUIRES_BLACK'
  | 'STRANDED'
  | 'NOT_STRANDED'
  | 'INVALID_STATION'
